import { MapPin } from "lucide-react";
import { useState } from "react";

import { CoachPhoto } from "../../components/coach-photo";
import { tag, well } from "../../components/section";
import { TimeChip } from "../../components/time-chip";
import type { PublicSpecialist, SlotPick } from "../../types";
import { timeLabel } from "../../utils/labels";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { getLocale } from "@/paraglide/runtime.js";

/**
 * How one week of open times looks. Lifted out of `open-calendar` so the platform's own landing
 * page can show the very same grid the booking pack shows a visitor, rather than a drawing of it
 * that drifts the moment either side changes.
 */

/** Times shown per day before the rest fold into "N more". */
export const VISIBLE_SLOTS = 3;

export const row = "grid gap-2.5 p-4 md:px-5 lg:grid-cols-[320px_repeat(var(--days),minmax(0,1fr))]";

export const dayLabel = (date: string, index: number, weeks: number) => {
  const day = new Date(`${date}T12:00:00`);
  return {
    number: day.getDate(),
    name:
      index === 0 && weeks === 0
        ? m.landing_calendar_today()
        : new Intl.DateTimeFormat(getLocale(), { weekday: "short" }).format(day),
  };
};

export const CoachRow = ({
  specialist,
  serviceName,
  placeOf,
  onPick,
}: {
  specialist: PublicSpecialist;
  serviceName: string | null;
  /** The name of the place a time is at. Null when the business has one place and nothing needs saying. */
  placeOf?: (locationId: string | null | undefined) => string | null;
  onPick: (pick: SlotPick) => void;
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={cn(row, well)}>
      {/* At the top of the row, and kept in view on a wide screen while a long day of times scrolls by. */}
      <div className="flex items-center gap-3.5 self-start lg:sticky lg:top-24">
        <CoachPhoto name={specialist.name} src={specialist.avatarUrl} className="size-16 rounded-[20px] text-xl" />
        <div className="min-w-0">
          <b className="block truncate text-[17px] font-medium tracking-[-0.01em]">{specialist.name}</b>
          <small className="block truncate text-[13px] text-muted-foreground">{specialist.title}</small>
          {serviceName ? <em className={cn(tag, "mt-1.5 not-italic")}>{serviceName}</em> : null}
        </div>
      </div>
      <div className="col-span-full grid grid-cols-3 gap-2.5 sm:grid-cols-5 lg:col-span-[var(--days)] lg:grid-cols-subgrid">
        {specialist.days.map((day, index) => {
          const open = day.slots.filter((slot) => slot.available);
          const shown = expanded ? open : open.slice(0, VISIBLE_SLOTS);
          const label = dayLabel(day.date, index, 1);
          // Where the coach is that day, said once above its times; a day split between places says each.
          const where = placeOf
            ? [...new Set(open.map((slot) => placeOf(slot.locationId)).filter((name): name is string => Boolean(name)))]
            : [];
          return (
            <div key={day.date} className="grid content-start gap-1.5">
              <p className="text-center text-xs text-muted-foreground lg:hidden">
                {label.name} {label.number}
              </p>
              {where.length > 0 ? (
                <p
                  className="flex min-h-5 items-center justify-center gap-1 truncate text-center text-[11px] font-medium text-muted-foreground"
                  data-slot="day-place"
                >
                  <MapPin aria-hidden className="size-3 shrink-0 text-[var(--site-ink)]" />
                  <span className="truncate">{where.join(" · ")}</span>
                </p>
              ) : null}
              {shown.length === 0 ? (
                <span aria-hidden className="grid h-10 place-items-center text-[var(--site-dot)]">
                  ·
                </span>
              ) : null}
              {shown.map((slot) => (
                <TimeChip
                  key={slot.startAt}
                  data-tour="calendar-slot"
                  className="bg-card px-0"
                  onClick={() =>
                    onPick({ specialistId: specialist.id, serviceId: specialist.serviceId, startAt: slot.startAt })
                  }
                >
                  {timeLabel(slot.startAt, specialist.timeZone)}
                </TimeChip>
              ))}
              {!expanded && open.length > VISIBLE_SLOTS ? (
                <button
                  type="button"
                  className="h-7 text-center text-xs text-muted-foreground transition-colors hover:text-foreground"
                  onClick={() => setExpanded(true)}
                >
                  {m.landing_calendar_more({ count: open.length - VISIBLE_SLOTS })}
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};
